import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "../ui/button";

interface PaginationProps {
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  /** The next page is loading. */
  readonly busy: boolean;
  readonly onPage: (page: number) => void;
  /** What is counted, e.g. "research" or "cards", for "No more … on this page". */
  readonly noun: string;
}

/** "Showing 1–25 of 30" with Previous and Next (shadcn-admin style). */
export function Pagination({ page, pageSize, total, busy, onPage, noun }: PaginationProps) {
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  const lastPage = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  return (
    <nav
      aria-label="Pages"
      className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground"
    >
      <span>
        {first > total
          ? `No more ${noun} on this page (${String(total)} in total).`
          : `Showing ${String(first)}–${String(last)} of ${String(total)}`}
      </span>
      {(lastPage > 1 || page > 1) && (
        <div className="flex items-center gap-2">
          <span className="hidden sm:inline">
            Page {Math.min(page, lastPage)} of {lastPage}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1 || busy}
            onClick={() => onPage(Math.min(page - 1, lastPage))}
          >
            <ChevronLeft aria-hidden="true" />
            Previous
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= lastPage || busy}
            onClick={() => onPage(page + 1)}
          >
            Next
            <ChevronRight aria-hidden="true" />
          </Button>
        </div>
      )}
    </nav>
  );
}
